import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAT_DEFS = [
  { key: "active_users",     label: "athletes active",   icon: "🦅" },
  { key: "signins",          label: "sign-ins",           icon: "🌅" },
  { key: "workouts",         label: "workouts logged",    icon: "💪" },
  { key: "meals",            label: "meals tracked",      icon: "🍎" },
  { key: "ai_plans",         label: "AI plans generated", icon: "🤖" },
  { key: "challenges",       label: "challenges claimed", icon: "🎯" },
  { key: "spins",            label: "wheel spins",        icon: "🎡" },
  { key: "jackpots",         label: "JACKPOTS hit",       icon: "🐦‍🔥", emphasize: true },
  { key: "badges",           label: "badges unlocked",    icon: "🏅" },
  { key: "collection_tiers", label: "mastery tiers",      icon: "👑", emphasize: true },
  { key: "friend_adds",      label: "new friends",        icon: "🤝" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function TodayOnOmnimuscle() {
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState([]);

  async function load() {
    try {
      const [summary, feedRes] = await Promise.all([
        axios.get(`${API}/today/summary`),
        axios.get(`${API}/today/feed`, { params: { limit: 8 } }),
      ]);
      setData(summary.data);
      setFeed(feedRes.data.feed || []);
    } catch (e) {
      setData(null);
    }
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(id);
  }, []);

  if (!data) return null;

  const visible = STAT_DEFS.filter((s) => (data[s.key] || 0) > 0);
  const totalActivity = visible.reduce((sum, s) => sum + (data[s.key] || 0), 0);

  return (
    <section
      data-testid="today-on-omnimuscle"
      className="bg-[#0A0A0A] border-y border-[#D4AF37]/30 py-10 px-4"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1 inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              Live · {data.date}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-white">
              Today on <span className="text-[#D4AF37]">OmniMuscle.</span>
            </h2>
          </div>
          {totalActivity > 0 && (
            <div className="text-right">
              <div className="font-mono tabular-nums font-bold text-3xl text-[#D4AF37]">
                {totalActivity.toLocaleString()}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                total moves today
              </div>
            </div>
          )}
        </div>

        {visible.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-8 italic">
            New UTC day — be the first to make a move. The phoenix is waiting.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {visible.map((s) => (
              <div
                key={s.key}
                data-testid={`today-stat-${s.key}`}
                className={`border p-4 transition-all ${
                  s.emphasize
                    ? "border-[#D4AF37] bg-[#D4AF37]/10"
                    : "border-zinc-800 bg-[#0F0F0F] hover:border-[#D4AF37]"
                }`}
              >
                <div className="text-2xl mb-1">{s.icon}</div>
                <div
                  className={`font-mono tabular-nums font-bold text-2xl leading-none ${
                    s.emphasize ? "text-[#D4AF37]" : "text-white"
                  }`}
                >
                  {(data[s.key] || 0).toLocaleString()}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Live Feed Ticker */}
        {feed.length > 0 && (
          <div data-testid="live-feed" className="mt-6 bg-[#0F0F0F] border border-zinc-900">
            <div className="px-4 py-2 border-b border-zinc-900 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold inline-flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                Live Feed
              </div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-600">
                last {feed.length}
              </div>
            </div>
            <ul className="divide-y divide-zinc-900">
              {feed.map((f, i) => (
                <li
                  key={i}
                  data-testid={`feed-item-${i}`}
                  className={`px-4 py-2.5 flex items-center gap-3 ${
                    f.emphasize ? "bg-[#D4AF37]/5" : ""
                  } animate-[fadeIn_0.4s_ease-in]`}
                >
                  <span className="text-xl flex-shrink-0">{f.icon}</span>
                  <div className="flex-1 min-w-0 text-sm">
                    <span
                      className={`font-bold ${f.emphasize ? "text-[#D4AF37]" : "text-white"}`}
                    >
                      {f.user_name}
                    </span>
                    <span className="text-neutral-400"> {f.label}</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-600 font-mono whitespace-nowrap">
                    {timeAgo(f.timestamp)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-[10px] text-neutral-600 uppercase tracking-widest mt-5 text-center">
          Auto-refreshes every 30s · resets at midnight UTC
        </p>
      </div>
    </section>
  );
}
